import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { LuPlus, LuX } from "react-icons/lu";
import zod from "zod";

import { PollWeightType } from "@/backend/focus";
import { useEditorFieldContext } from "@/components/app/editor-v3/editor-context";
import UserSelect from "@/components/shared/users-search/user-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DESO_APP_NAME } from "@/utils/constants";
import { flattenErrorMessages } from "@/utils/form";

const SelectedAccountSchema = zod
  .object({
    publicKey: zod.string(),
    username: zod.string().nullable().optional(),
  })
  .passthrough()
  .nullable();

const FormSchema = zod.object({
  weightType: zod.string().min(1),
  selectedAccount: SelectedAccountSchema,
  options: zod
    .array(
      zod.object({
        value: zod.string().min(1, "Option cannot be empty!"),
      }),
    )
    .superRefine((arr, ctx) => {
      const valueIndices = new Map<string, number[]>();
      arr.forEach((opt, i) => {
        const indices = valueIndices.get(opt.value) ?? [];
        indices.push(i);
        valueIndices.set(opt.value, indices);
      });
      valueIndices.forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((i) => {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: "Option values must be unique!",
              path: [i, "value"],
            });
          });
        }
      });
    }),
});

const NEW_OPTION = { value: "" };

export type PollFormValues = zod.infer<typeof FormSchema>;

const POLL_WEIGHT_TYPE_LABELS = {
  [PollWeightType.unweighted]: "Simple poll",
  [PollWeightType.desoBalance]: `Weight By ${DESO_APP_NAME} Balance`,
  [PollWeightType.desoTokenBalance]: "Weight By Token Balance",
};
const POLL_WEIGHT_TYPE_LABELS_SELECTED = {
  [PollWeightType.unweighted]: "Simple poll",
  [PollWeightType.desoBalance]: `${DESO_APP_NAME} Balance`,
  [PollWeightType.desoTokenBalance]: "Token Balance",
};

const MAX_OPTIONS = 20;

export const EditorPollForm = () => {
  const { value, setEditorValue, errors, addErrors } =
    useEditorFieldContext<"poll">();

  const form = useForm<PollFormValues>({
    mode: "onChange",
    resolver: zodResolver(FormSchema),
    defaultValues: value || {
      weightType: PollWeightType.unweighted,
      selectedAccount: null,
      options: [{ value: "" }, { value: "" }],
    },
  });
  const { control, register, setValue, watch, formState } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });
  const allFields = watch();
  const selectedAccount = allFields.selectedAccount;

  const debounceRef = useRef<NodeJS.Timeout>(null!);

  useEffect(() => {
    if (
      allFields.weightType !== PollWeightType.desoTokenBalance &&
      selectedAccount !== null
    ) {
      setValue("selectedAccount", null);
    }
  }, [allFields.weightType, selectedAccount, setValue]);

  useEffect(() => {
    const hasError =
      Object.keys(formState.errors).length > 0 ||
      !allFields.weightType ||
      (allFields.weightType === PollWeightType.desoTokenBalance &&
        selectedAccount === null);

    if (Object.keys(formState.errors).length > 0) {
      addErrors(flattenErrorMessages(formState.errors));
    }

    if (
      allFields.weightType === PollWeightType.desoTokenBalance &&
      selectedAccount === null
    ) {
      addErrors(["Please select an account for token balance weighting."]);
    }

    if (!hasError) {
      errors.clear();
    }
  }, [formState.errors]);

  return (
    <Form {...form}>
      <form
        className="flex flex-col items-stretch gap-2"
        onSubmit={(event) => {
          // This panel only updates editor context. If the browser performs a
          // native submit here, field-array names like `options.0.value` leak
          // into the page URL and every Next server action reuses that URL.
          event.preventDefault();
        }}
        onChange={() => {
          clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            form.trigger();
            setEditorValue(allFields);
          }, 200);
        }}
      >
        <div className="flex flex-col gap-4 py-2">
          <FormField
            control={form.control}
            name="weightType"
            render={({ field }) => (
              <FormItem
                className="
                  flex h-10 w-full flex-col items-start gap-2 text-left text-sm
                "
              >
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full rounded-full py-[19px]">
                      <SelectValue placeholder="Select a poll type from the list" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {Object.values(PollWeightType).map((type) => {
                      const label =
                        field.value === type
                          ? POLL_WEIGHT_TYPE_LABELS_SELECTED[type]
                          : POLL_WEIGHT_TYPE_LABELS[type];

                      return (
                        <SelectItem key={type} value={type}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {allFields.weightType === PollWeightType.desoTokenBalance && (
            <FormControl>
              <UserSelect
                selectedAccount={selectedAccount}
                onSelect={(e) => {
                  if (selectedAccount === e) {
                    // do not trigger form validation if nothing changed
                    return;
                  }

                  form.setValue(
                    "selectedAccount",
                    e?.publicKey
                      ? {
                          publicKey: e.publicKey,
                          username: e.username ?? null,
                        }
                      : null,
                  );
                }}
              />
            </FormControl>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {fields.map((field, index) => {
            const showRemoveButton = index > 1;
            const placeholder = `Option ${index + 1}${showRemoveButton ? " (Optional)" : ""}`;
            const error = formState.errors.options?.[index]?.value?.message;
            return (
              <div key={field.id}>
                <div className="flex justify-between gap-2">
                  <Input
                    placeholder={placeholder}
                    {...register(`options.${index}.value`)}
                  />
                  {showRemoveButton && (
                    <Button
                      onClick={() => remove(index)}
                      variant="outline"
                      className="w-[35px] grow p-0 text-muted"
                    >
                      <LuX />
                    </Button>
                  )}
                </div>
                {error && (
                  <p className="pt-1 pl-1 text-xs text-red-300">{error}</p>
                )}
              </div>
            );
          })}
        </div>
        {allFields.options.length <= MAX_OPTIONS && (
          <div className="mt-2">
            <Button
              variant="outline"
              className="flex w-full items-center gap-2"
              size="sm"
              onClick={() => append(NEW_OPTION)}
            >
              <LuPlus className="text-base text-muted" />
              Add Option
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};
